import { useState } from "react";
import { Link } from "react-router-dom";
import { usePortfolio, groupByService } from "@/data/portfolio";
import PdfViewer from "@/components/PdfViewer";
import { ArrowRight, Eye, FileText, Lock } from "@phosphor-icons/react";

export default function Portfolio() {
  const { items, loaded } = usePortfolio();
  const [active, setActive] = useState(null);
  const [filter, setFilter] = useState("all");

  const filtered = filter === "all" ? items : items.filter((i) => i.service === filter);
  const groups = groupByService(filtered);

  return (
    <div data-testid="portfolio-page">
      <section className="relative bg-zinc-50 border-b border-zinc-200 overflow-hidden ruler-top">
        <div className="absolute inset-0 bg-blueprint-grid pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-14 md:pb-20">
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#0044FF] mb-4">
            Portfolio · Sample work
          </div>
          <h1 className="font-display text-5xl md:text-7xl tracking-tighter font-medium text-zinc-950 max-w-4xl leading-[0.95]">
            See how we draft.
          </h1>
          <p className="mt-6 text-lg text-zinc-600 max-w-2xl leading-relaxed">
            Sample exhibits from real projects, redacted of client-identifying data. View-only —
            <span className="inline-flex items-center gap-1 ml-1">
              <Lock size={13} weight="bold" className="text-[#0044FF]" /> downloads disabled
            </span>.
          </p>
          <div className="mt-8 flex flex-wrap gap-2" data-testid="portfolio-filters">
            <FilterChip on={filter === "all"} onClick={() => setFilter("all")}>All ({items.length})</FilterChip>
            {Array.from(new Set(items.map((i) => i.service))).map((svc) => (
              <FilterChip
                key={svc}
                on={filter === svc}
                onClick={() => setFilter(svc)}
                testId={`filter-${svc}`}
              >
                {(items.find((i) => i.service === svc) || {}).service_title || svc}
              </FilterChip>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-zinc-50 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 space-y-16">
          {!loaded && (
            <div className="text-zinc-500 text-sm font-mono">Loading portfolio…</div>
          )}
          {loaded && items.length === 0 && (
            <div className="bg-white border border-zinc-200 p-10 text-center">
              <FileText size={36} weight="duotone" className="text-zinc-400 mx-auto" />
              <h3 className="font-display text-2xl text-zinc-950 mt-4">No portfolio items yet</h3>
              <p className="text-sm text-zinc-600 mt-2 max-w-md mx-auto">
                New drawing samples are on their way. Check back soon.
              </p>
            </div>
          )}
          {groups.map((g) => (
            <div key={g.slug}>
              <div className="flex items-end justify-between mb-6">
                <div>
                  <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#0044FF] mb-2">
                    {g.items.length} sample{g.items.length === 1 ? "" : "s"}
                  </div>
                  <h2 className="font-display text-3xl md:text-4xl tracking-tight text-zinc-950">
                    {g.title}
                  </h2>
                </div>
                <Link
                  to={`/services/${g.slug}`}
                  className="text-sm font-medium text-[#0044FF] hover:underline inline-flex items-center gap-1.5"
                >
                  Service scope <ArrowRight size={12} weight="bold" />
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
                {g.items.map((it, idx) => (
                  <button
                    key={it.slug}
                    onClick={() => setActive(it)}
                    className="group bg-white text-left hover:bg-zinc-50 transition-colors block"
                    data-testid={`portfolio-item-${it.slug}`}
                  >
                    <div
                      className="aspect-[4/3] bg-zinc-100 bg-contain bg-no-repeat bg-center relative overflow-hidden"
                      style={it.thumb_url ? { backgroundImage: `url(${it.thumb_url})` } : {}}
                    >
                      <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/60 via-transparent to-transparent" />
                      <div
                        className="absolute inset-0 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity bg-zinc-950/40"
                      >
                        <div className="flex items-center gap-2 text-sm font-medium">
                          <Eye size={16} weight="duotone" /> View sample
                        </div>
                      </div>
                      <div className="absolute top-3 left-3 text-[10px] font-mono uppercase tracking-[0.25em] text-white/90 bg-zinc-950/40 px-2 py-1">
                        FIG {String(idx + 1).padStart(2, "0")}
                      </div>
                    </div>
                    <div className="p-5">
                      <div className="font-display text-lg text-zinc-950 tracking-tight leading-tight">
                        {it.title}
                      </div>
                      <div className="mt-1 text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500">
                        {it.state || "USA"} · {it.year || ""}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {active && <PdfViewer item={active} onClose={() => setActive(null)} />}
    </div>
  );
}

function FilterChip({ children, on, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-1.5 text-xs font-medium border transition-colors ${
        on ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-900"
      }`}
    >
      {children}
    </button>
  );
}
